"use client";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchVideos } from "../../Redux/Reducers/VideoReducer";
import { Link, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  TablePagination,
  TextField,
} from "@mui/material";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import toast from "react-hot-toast";
import { getIntParam } from "../../utils/urlState";

function VideoList() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitUrlSyncRef = React.useRef(false);
  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });
  const videoData = useSelector((state) => state?.VideoReducer?.videos);
  const pagination = useSelector((state) => state?.VideoReducer?.pagination);
  const mainTitle = useSelector((state) => state?.VideoReducer?.mainTitle) || "No main title";
  const videoLoading = useSelector((state) => state?.VideoReducer?.loading);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [updateMainTitle, setUpdateMainTitle] = useState(false);
  const [mainTitleInput, setMainTitleInput] = useState("");
  const [mainTitleLoading, setMainTitleLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  useEffect(() => {
    const { page, pageSize } = paginationModel;
    let limit = Number(pageSize);
    if (isNaN(limit)) {
      limit = pagination?.totalDocs || 10000;
    }
    dispatch(fetchVideos({ page: page + 1, limit: limit }));
  }, [paginationModel.page, paginationModel.pageSize, dispatch]);

  // Keep URL in sync so Back restores pagination.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, setSearchParams]);

  // Dispatch fetchVideos when mainTitle changes (to refresh the list with updated mainTitle)
  useEffect(() => {
    if (mainTitle && mainTitle !== "No main title") {
      const { page, pageSize } = paginationModel;
      let limit = Number(pageSize);
      if (isNaN(limit)) {
        limit = pagination?.totalDocs || 10000;
      }
      dispatch(fetchVideos({ page: page + 1, limit: limit }));
    }
  }, [mainTitle]);

  const handleClickOpen = (userId) => {
    setSelectedUserId(userId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePreviewOpen = (video) => {
    setPreviewVideo(video);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewVideo(null);
  };

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleChangePage = (event, newPage) => {
    setPaginationModel({ ...paginationModel, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    const newPageSize = parseInt(value, 10);
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  const handleDelete = async () => {
    setLoading(true);
    const token = localStorage.getItem("astroVani-admin-token");

    try {
      await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/video/${selectedUserId}`, {
        headers: {
          Authorization: `${token}`,
        },
      });

      toast.success("Video deleted successfully!");

      const { page, pageSize } = paginationModel;
      let limit = Number(pageSize);
      if (isNaN(limit)) {
        limit = pagination?.totalDocs || 10000;
      }
      dispatch(fetchVideos({ page: page + 1, limit: limit }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete video. Please try again.");
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleUpdateMainTitle = async () => {
    if (!mainTitleInput.trim()) {
      toast.error("Main Title cannot be empty");
      return;
    }

    setMainTitleLoading(true);
    const token = localStorage.getItem("astroVani-admin-token");

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASEURL}/admin/video/mainTitle`,
        { mainTitle: mainTitleInput },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        toast.success("Main Title updated successfully!");
        setUpdateMainTitle(false);
        // The mainTitle in Redux will be updated, which will trigger fetchVideos via useEffect
      } else {
        throw new Error(response.data?.message || "Failed to update main title");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update main title. Please try again.");
    } finally {
      setMainTitleLoading(false);
      dispatch(fetchVideos({ page: 1, limit: 10 }));
    }
  };

  const columns = [
    {
      field: "sono",
      headerName: "S.No",
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => {
        const video = row.videoData || videoData?.find(v => v._id === row.id);
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <IconButton
              size="small"
              onClick={() => handlePreviewOpen(video)}
              title="Preview Video"
              sx={{
                color: '#28a745',
                '&:hover': {
                  backgroundColor: 'rgba(40, 167, 69, 0.08)',
                },
              }}
            >
              <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
            </IconButton>
            <IconButton
              size="small"
              component={Link}
              to={`/video-edit/${row?.id}`}
              title="Edit Video"
              sx={{
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <i className="icofont-edit" style={{ fontSize: '18px' }}></i>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleClickOpen(row?.id)}
              title="Delete Video"
              sx={{
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                },
              }}
            >
              <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
            </IconButton>
          </Box>
        );
      },
    },
    { field: "title", headerName: "Title", width: 250 },
    {
      field: "videoUrl",
      headerName: "Video URL",
      width: 300,
      renderCell: (params) => (
        <a
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'none' }}
        >
          {params.value?.length > 40 ? params.value.substring(0, 40) + '...' : params.value}
        </a>
      )
    },
    { field: "createdAt", headerName: "Created At", width: 200, type: 'dateTime' },
    { field: "updatedAt", headerName: "Updated At", width: 200, type: 'dateTime' },
  ];

  const rows = videoData?.map((video, index) => {
    const pageSize = paginationModel.pageSize;
    return {
      sono: paginationModel.page * pageSize + index + 1,
      id: video._id,
      title: video.title,
      mainTitle: video.mainTitle,
      videoUrl: video.videoUrl,
      videoId: video.videoId,
      createdAt: video?.createdAt ? new Date(video?.createdAt) : null,
      updatedAt: video?.updatedAt ? new Date(video?.updatedAt) : null,
      videoData: video, // Store full video object for preview
    };
  }) || [];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 righttitle="Add Video" link="/video-add" routebutton={true} pagetitle='Youtube Videos List' />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card" style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
              overflow: 'hidden'
            }}>
              <div className="card-body" style={{ padding: 0, position: 'relative' }}>
                <Box sx={{
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-cell[data-field="actions"]': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-footerContainer': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                }}>
                  {mainTitle && (
                    <div className="p-3 border-bottom bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold text-dark" style={{ fontSize: '16px' }}>Main Title:</span>
                          <span className="text-muted" style={{ fontSize: '16px' }}>{mainTitle}</span>
                        </div>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<i className="icofont-edit"></i>}
                          onClick={() => {
                            setMainTitleInput(mainTitle);
                            setUpdateMainTitle(true);
                          }}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            backgroundColor: "var(--primary-color, #1976d2)",
                            color: "#fff",
                            height: 30,
                            px: 2,
                            flexShrink: 0,
                            "&:hover": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.9 },
                            "&:disabled": {
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                              color: "rgba(0, 0, 0, 0.26)",
                            },
                          }}
                        >
                          Update Main Title
                        </Button>
                      </div>
                    </div>
                  )}
                  <DataGrid
                    columns={columns}
                    rows={rows}
                    loading={videoLoading}
                    disableSelectionOnClick
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) =>
                      setColumnVisibilityModel(newModel)
                    }
                    paginationMode="server"
                    hideFooterPagination
                    getRowId={(row) => row.id}
                    autoHeight
                    sx={{
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                    }}
                  />
                </Box>
                <TablePagination
                  component="div"
                  count={pagination?.totalDocs || 0}
                  page={paginationModel.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    borderTop: '1px solid #e9ecef',
                    '& .MuiTablePagination-toolbar': {
                      padding: '12px 20px',
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={updateMainTitle}
        onClose={() => setUpdateMainTitle(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minHeight: '280px',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          fontWeight: 500,
          fontSize: '20px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          Update Main Title
        </DialogTitle>
        <DialogContent sx={{ pt: 5, pb: 0, minHeight: '80px' }}>
          <TextField
            label="Main Title"
            value={mainTitleInput}
            onChange={(e) => setMainTitleInput(e.target.value)}
            fullWidth
            variant="outlined"
            autoFocus
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                minHeight: '56px',
              },
              '& .MuiInputBase-input': {
                padding: '16px 14px',
                fontSize: '16px',
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setUpdateMainTitle(false)}
            disabled={mainTitleLoading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              minWidth: '100px',
              borderRadius: '6px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateMainTitle}
            variant="contained"
            color="primary"
            disabled={mainTitleLoading}
            sx={{
              textTransform: 'none',
              minWidth: '100px',
              borderRadius: '6px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              }
            }}
          >
            {mainTitleLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          pt: 3,
          fontWeight: 400,
          fontSize: '20px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          {previewVideo?.title || 'Video Preview'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {previewVideo?.videoUrl && (
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px' }}>
              <iframe
                src={previewVideo.videoUrl.includes('embed')
                  ? previewVideo.videoUrl
                  : `https://www.youtube.com/embed/${getYouTubeVideoId(previewVideo.videoUrl) || previewVideo.videoId || ''}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={previewVideo.title}
              />
            </div>
          )}
          {previewVideo?.title && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                <strong>Title:</strong> {previewVideo.title}
              </p>
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={handlePreviewClose}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              minWidth: '100px',
              borderRadius: '6px',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this video?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default VideoList;

